import { Inject, Injectable, inject } from '@angular/core';
import { startCase } from 'lodash';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { CmsContentService } from '@digital-platform/rs-cms/services';
import {
  LocalPagePathConstants,
  localPagePathsConstantsToken,
} from '../../constants/local-pages-paths.constants';
import {
  LocalPageZipsConstants,
  localPageZipConstantsToken,
} from '../../constants/local-pages-zips.constants';
import {
  PageUrlConstants,
  pageUrlConstantsToken,
} from '../../constants/page-url.constants';
import {
  StatesConstants,
  statesConstantsToken,
} from '../../constants/states.constants';

/**
 * LocationsUtilsService
 *
 * Service used to perform string/URL transformations on the /locations paths
 */
@Injectable({
  providedIn: 'root',
})
export class LocationsUtilsService {
  constructor(
    @Inject(localPagePathsConstantsToken)
    private readonly localPagesPaths: LocalPagePathConstants,
    @Inject(localPageZipConstantsToken)
    private readonly localPagesZips: LocalPageZipsConstants,
    @Inject(pageUrlConstantsToken)
    private readonly pageUrlConstants: PageUrlConstants,
    @Inject(statesConstantsToken)
    private readonly statesConstants: StatesConstants
  ) {}

  private cmsService = inject(CmsContentService);

  /*
   * Build the /locations url based on the location
   * components passed in. Each component is optional.
   * The return  will always begin with the base relative
   * url (/locations). Each component will be encoded.
   */
  public formatUrl(
    state?: string,
    city?: string,
    postalCode?: string
  ): string | undefined {
    const baseUrl = '/locations';

    if (!state && !city && !postalCode) {
      return baseUrl;
    }

    if (postalCode) {
      return this.getLocationUrl(state, city, postalCode);
    }

    if (state && !city) {
      return this.getLocationUrl(state);
    }

    return '';
  }

  public formatUrlByCityAndState(
    state: string,
    city: string
  ): Observable<string> {
    const lState = state.toLowerCase();
    const currentStateCode: string =
      Object.keys(this.statesConstants).find(
        (stateCode: string): boolean =>
          this.statesConstants[
            stateCode as keyof typeof this.statesConstants
          ].toLowerCase() === lState
      ) || '';
    const lcurrentStateCode = currentStateCode?.toLowerCase();
    const encodedCity = this.encodeLocationUri(city);
    const encodedState = this.encodeLocationUri(state);
    const municipalityUrl = `/municipality/${encodedCity}-${lcurrentStateCode}`;
    const trashPickupUrl = `/locations/${encodedState}/${encodedCity}-trash-pickup-and-recycling`;

    return this.cmsService.getPageAsJson(municipalityUrl).pipe(
      map((): string => municipalityUrl),
      catchError((): Observable<string> => of(trashPickupUrl))
    );
  }

  /*
   * Encode the location element by changing case to lower
   * and replacing whitespce with dashes
   */
  public encodeLocationUri(location: string): string {
    return location.toLowerCase().replace(/ /g, '-');
  }

  /*
   * Decode the location element by replacing dashes to whitespace
   */
  public decodeLocationUri(encodedUri: string): string {
    return startCase(encodedUri);
  }

  public getLocalPageCmsPath(postalCode: string):
    | {
        city: string;
        state: string;
        redirectUrl: string;
      }
    | undefined {
    const cmsPathDetails = Object.entries(this.localPagesPaths).find(
      ([key, _path]) =>
        this.localPagesZips[key as keyof typeof this.localPagesZips] &&
        this.localPagesZips[key as keyof typeof this.localPagesZips].includes(
          postalCode
        )
    );

    if (Array.isArray(cmsPathDetails) && cmsPathDetails.length > 0) {
      return cmsPathDetails[1];
    } else {
      return undefined;
    }
  }

  private getLocationUrl(
    state?: string,
    city?: string,
    postalCode?: string
  ): string {
    let url = this.pageUrlConstants.LOCATIONS;

    if (state) {
      url += `/${this.encodeLocationUri(state)}`;

      if (city) {
        url += `/${this.encodeLocationUri(city)}`;

        if (postalCode) {
          url += `/${this.encodeLocationUri(postalCode)}`;
        }
      }
    }

    return url;
  }
}
